import { useTranslation } from 'react-i18next';
import { CheckCircle, Lock, Play, Clock } from 'lucide-react';
import { Btn } from '@/presentation/component/ui';
import { formatCFA } from '@/presentation/util/format';
import type { Chapter } from '@/domain/model/Chapter';
import type { Course } from '@/domain/model/Course';
import type { CourseProgress } from '@/domain/model/CourseProgress';
import { hasCourseAccess } from '@/domain/model/accessUtils';
import {
  coursePercentage,
  isCourseCompleted,
  overallPercentage,
} from '@/domain/model/courseProgressUtils';
import { MediaTypeLabel } from '@/presentation/component/course/MediaTypeLabel';
import { CourseProgressBar } from '@/presentation/component/course/CourseProgressBar';

interface ChapterSidebarProps {
  subcategoryName: string;
  subcategoryId: string;
  subcategoryPrice: number;
  chapters: Chapter[];
  currentCourse: Course;
  onCourseSelect: (course: Course) => void;
  progressByCourseId: Record<string, CourseProgress>;
  hasAccess: boolean;
}

interface CourseListItemProps {
  course: Course;
  position: number;
  isCurrent: boolean;
  isLocked: boolean;
  isCompleted: boolean;
  percentage: number;
  onSelect: (course: Course) => void;
}

const formatLessonDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const CourseListItem = ({
  course,
  position,
  isCurrent,
  isLocked,
  isCompleted,
  percentage,
  onSelect,
}: CourseListItemProps) => {
  const { t } = useTranslation();
  const isStarted = percentage > 0 && !isCompleted;

  return (
    <button
      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 text-left ${
        isCurrent
          ? 'bg-[var(--color-accent)] text-[var(--color-on-accent)]'
          : 'bg-[var(--color-surface)] text-[var(--color-ink-soft)] hover:bg-[var(--color-surface-3)]'
      }`}
      onClick={() => !isLocked && onSelect(course)}
      disabled={isLocked}
    >
      <span className="shrink-0 w-6 h-6 text-xs font-bold flex items-center justify-center">
        {position}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium truncate">{course.title}</h4>
          {isCompleted && <CheckCircle className="w-4 h-4 text-[var(--color-good)] shrink-0" />}
          {isLocked && <Lock className="w-4 h-4 text-[var(--color-ink-faint)] shrink-0" />}
        </div>
        <div className="flex items-center gap-2 text-sm opacity-75">
          <Clock className="w-3 h-3" />
          <span>{formatLessonDuration(course.duration)}</span>
          <MediaTypeLabel mediaType={course.mediaType} />
        </div>
        {isStarted ? (
          <>
            <span className="sr-only">
              {t('coursePlayer.lessonProgressValue', { percentage: Math.round(percentage) })}
            </span>
            <CourseProgressBar
              percentage={percentage}
              className="mt-2 h-1 bg-current/25"
              barClassName="bg-current"
            />
          </>
        ) : null}
      </div>

      {isCurrent && <Play className="w-4 h-4 shrink-0" />}
    </button>
  );
};

const PurchaseCallout = ({
  subcategoryId,
  subcategoryPrice,
}: Pick<ChapterSidebarProps, 'subcategoryId' | 'subcategoryPrice'>) => {
  const { t } = useTranslation();

  return (
    <div className="p-4 border-t border-[var(--color-line)]">
      <div className="bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-accent-hover)] rounded-lg p-4 text-[var(--color-on-accent)]">
        <h3 className="font-semibold mb-2">{t('category.unlockFullAccess')}</h3>
        <p className="text-sm opacity-90 mb-3">{t('category.unlockDescription')}</p>
        <a href={`/subcategory/${subcategoryId}`}>
          <Btn
            kind="ghost"
            className="w-full border-[var(--color-on-accent)]/30 text-[var(--color-on-accent)] hover:bg-[var(--color-on-accent)]/10"
          >
            {t('buttons.purchase')} - {formatCFA(subcategoryPrice)}
          </Btn>
        </a>
      </div>
    </div>
  );
};

export const ChapterSidebar = ({
  subcategoryName,
  subcategoryId,
  subcategoryPrice,
  chapters,
  currentCourse,
  onCourseSelect,
  progressByCourseId,
  hasAccess,
}: ChapterSidebarProps) => {
  const { t } = useTranslation();

  const courseIds = chapters.flatMap(
    (chapter) => chapter.courses?.map((course) => course.id) || [],
  );
  const progressPercent = overallPercentage(progressByCourseId, courseIds);

  return (
    <div className="h-full bg-[var(--color-surface-2)] overflow-y-auto">
      <div className="p-4 border-b border-[var(--color-line)]">
        <h2 className="text-lg font-semibold text-[var(--color-ink)] mb-2">{subcategoryName}</h2>
        <CourseProgressBar
          percentage={progressPercent}
          label={t('coursePlayer.courseProgress')}
          className="h-2 bg-[var(--color-surface-3)]"
        />
        <p className="text-sm text-[var(--color-ink-faint)] mt-2">
          {t('coursePlayer.completionRate')}: {Math.round(progressPercent)}%
        </p>
      </div>

      <div className="p-4 space-y-6">
        {chapters.map((chapter, chapterIndex) => (
          <div key={chapter.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-[var(--color-accent)] rounded-full flex items-center justify-center text-[var(--color-on-accent)] text-sm font-bold shrink-0">
                {chapterIndex + 1}
              </div>
              <h3 className="text-[var(--color-ink)] font-medium">{chapter.name}</h3>
            </div>

            <div className="ml-8 space-y-2">
              {chapter.courses?.map((course, courseIndex) => (
                <CourseListItem
                  key={course.id}
                  course={course}
                  position={courseIndex + 1}
                  isCurrent={currentCourse.id === course.id}
                  isLocked={!hasCourseAccess(course, hasAccess)}
                  isCompleted={isCourseCompleted(progressByCourseId, course.id)}
                  percentage={coursePercentage(progressByCourseId, course.id)}
                  onSelect={onCourseSelect}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {!hasAccess ? (
        <PurchaseCallout subcategoryId={subcategoryId} subcategoryPrice={subcategoryPrice} />
      ) : null}
    </div>
  );
};
